import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  RelatedGenuinePart,
  RelatedGenuinePartsDTO,
} from "./types/related-genuine-parts.types";
import Slider from "react-slick";
import { useTranslation } from "react-i18next";
import "./RelatedGenuinePartsByCategoryResolver.scss";

interface RelatedGenuinePartsByCategoryResolverProps {
  fields: {
    filterUrl: string;
    suggestedProducts?: RelatedGenuinePart[];
  };
}

const MAX_ITEMS = 10;

const normalizeId = (id?: string | null) => (id ?? "").toLowerCase();

const extractExcludeIdFromFilterUrl = (filterUrl: string): string | null => {
  const match = filterUrl.match(/exclude=\{?([0-9a-fA-F-]{36})\}?/);
  return match?.[1] ?? null;
};

const applyRules = (
  items: RelatedGenuinePart[],
  excludeId: string | null
): RelatedGenuinePart[] => {
  const seen = new Set<string>();

  return items
    .filter((p) => {
      const id = normalizeId(p.id);
      if (!id) return false;
      if (excludeId && id === normalizeId(excludeId)) return false;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .slice(0, MAX_ITEMS);
};

const RelatedGenuinePartsByCategoryResolver: FC<
  RelatedGenuinePartsByCategoryResolverProps
> = ({ fields }) => {
  const { t } = useTranslation();

  const [relatedParts, setRelatedParts] = useState<RelatedGenuinePart[]>([]);

  const excludeId = useMemo(
  () => extractExcludeIdFromFilterUrl(fields.filterUrl),
  [fields.filterUrl]
);

const fetchRelatedParts = useCallback(async () => {
  if (fields.suggestedProducts && fields.suggestedProducts.length > 0) {
    setRelatedParts(applyRules(fields.suggestedProducts, excludeId));
    return;
  }

  const res = await axios.get<RelatedGenuinePartsDTO>(fields.filterUrl);
  const automatic = res?.data?.teasers ?? [];
  setRelatedParts(applyRules(automatic, excludeId));
}, [fields.filterUrl, fields.suggestedProducts, excludeId]);

useEffect(() => {
  fetchRelatedParts();
}, [fetchRelatedParts]);

  const sliderRef = useRef<Slider | null>(null);

  const handlePrev = (): void => {
    sliderRef.current?.slickPrev();
  };

  const handleNext = (): void => {
    sliderRef.current?.slickNext();
  };

  const itemsAmount = useMemo(() => relatedParts.length, [relatedParts]);

  const settings = {
    dots: true,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToScroll: 1,
    variableWidth: true,
  };

  return (
    <div
      id="related-products-by-category"
      className="RelatedGenuinePartsByCategoryResolver"
      >
      <div>
        <h2 className="RelatedGenuinePartsByCategoryResolver__title">
          {t("OtherProductsTitle")}
        </h2>

        <button
          type="button"
          className="slider-arrow prev"
          onClick={handlePrev}
          aria-label="Previous"
          style={
            itemsAmount <= 4 ? { display: "none", visibility: "hidden" } : null
          }
        >
          <svg
            width="12"
            height="21"
            viewBox="0 0 12 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M8.74218e-07 10.4069L0.982644 11.4486L9.86128 20.8125L12 18.729L4.10393 10.4068L12 2.08461L9.86129 0.00115484L0.982644 9.36501L8.74218e-07 10.4069Z"
              fill="white"
            />
          </svg>
        </button>

        <button
          type="button"
          className="slider-arrow next"
          onClick={handleNext}
          aria-label="Next"
          style={
            itemsAmount <= 4 ? { display: "none", visibility: "hidden" } : null
          }
        >
          <svg
            width="12"
            height="21"
            viewBox="0 0 12 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12 10.4069L11.0174 11.4486L2.13872 20.8125L-1.75041e-07 18.729L7.89607 10.4068L-1.57341e-06 2.08461L2.13871 0.00115484L11.0174 9.36501L12 10.4069Z"
              fill="white"
            />
          </svg>
        </button>

        <div
          className="RelatedGenuinePartsByCategoryResolver__slider_container"
          style={
            itemsAmount < 4 && itemsAmount > 0
              ? { maxWidth: 273 * itemsAmount + 16 * (itemsAmount - 1) }
              : null
          }
        >
          {relatedParts.length > 0 && (
            <Slider ref={sliderRef} {...settings}>
              {relatedParts.map((part) => (
                <RelatedPartCard key={part.id} part={part} />
              ))}
            </Slider>
          )}
        </div>
      </div>
    </div>
  );
};

const RelatedPartCard: FC<{ part: RelatedGenuinePart }> = memo(
  function RelatedPartCard({ part }) {
    const { t } = useTranslation();

    return (
      <div className="RelatedPartCard">
        <div>
          <h3 className="RelatedPartCard__title">
            {part?.fields?.teaserTitle?.value}
          </h3>

          <div className="RelatedPartCard__image">
            <img
              loading="lazy"
              src={part?.fields?.teaserImage?.value?.src}
              alt={part?.fields?.teaserImage?.value?.alt}
            />
          </div>

          {part?.fields?.compatibleProducts.length > 0 && (
            <>
              <p className="RelatedPartCard__compatible_label">
                {t("CompatibleProducts")}
              </p>
              <p className="RelatedPartCard__compatible_value">
                {part?.fields?.compatibleProducts
                  ?.map((i) => i.displayName)
                  .join(", ")}
              </p>
            </>
          )}
        </div>

        <a className="RelatedPartCard__more ga4-click-ui-correlati" href={part?.itemUrl}>
          {t("ShowProduct")}
        </a>
      </div>
    );
  }
);

export default RelatedGenuinePartsByCategoryResolver;
